export type BalanceLine = {
  companyId: number;
  debitLocal: number | string;
  creditLocal: number | string;
};

export function toNumber(value: number | string | null | undefined): number {
  if (value == null || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function sumDebitCredit(lines: BalanceLine[]) {
  let debit = 0;
  let credit = 0;
  for (const line of lines) {
    debit += toNumber(line.debitLocal);
    credit += toNumber(line.creditLocal);
  }
  return { debit, credit, diff: Math.round((debit - credit) * 100) / 100 };
}

export type UsdBalanceLine = {
  debitUSD: number | string;
  creditUSD: number | string;
};

export function sumDebitCreditUsd(lines: UsdBalanceLine[]) {
  let debit = 0;
  let credit = 0;
  for (const line of lines) {
    debit += toNumber(line.debitUSD);
    credit += toNumber(line.creditUSD);
  }
  return { debit, credit, diff: Math.round((debit - credit) * 100) / 100 };
}

export function perCompanyBalances(lines: BalanceLine[]) {
  const map = new Map<number, { debit: number; credit: number }>();
  for (const line of lines) {
    const cur = map.get(line.companyId) ?? { debit: 0, credit: 0 };
    cur.debit += toNumber(line.debitLocal);
    cur.credit += toNumber(line.creditLocal);
    map.set(line.companyId, cur);
  }
  return Array.from(map.entries()).map(([companyId, totals]) => ({
    companyId,
    debit: totals.debit,
    credit: totals.credit,
    diff: Math.round((totals.debit - totals.credit) * 100) / 100,
  }));
}

export type CompanyBalance = ReturnType<typeof perCompanyBalances>[number];

export function isBalanced(lines: BalanceLine[], tolerance = 0.01): boolean {
  const global = sumDebitCredit(lines);
  if (Math.abs(global.diff) > tolerance) return false;
  return perCompanyBalances(lines).every((row) => Math.abs(row.diff) <= tolerance);
}

export function localToUsdAmount(
  local: number | string | null | undefined,
  rate: number | string | null | undefined,
): number {
  const localN = toNumber(local);
  const rateN = toNumber(rate);
  if (localN <= 0 || rateN <= 0) return 0;
  return Math.round(localN * rateN * 100) / 100;
}

export function formatLocalToUsd(
  local: number | string | null | undefined,
  rate: number | string | null | undefined,
): string {
  const usd = localToUsdAmount(local, rate);
  return usd > 0 ? String(usd) : '';
}

export function formatUsdToLocal(
  usd: number | string | null | undefined,
  rate: number | string | null | undefined,
): string {
  const usdN = toNumber(usd);
  const rateN = toNumber(rate);
  if (usdN <= 0 || rateN <= 0) return '';
  return String(Math.round((usdN / rateN) * 100) / 100);
}

export function usdToLocalAmount(
  usd: number | string | null | undefined,
  rate: number | string | null | undefined,
): number {
  const usdN = toNumber(usd);
  const rateN = toNumber(rate);
  if (usdN <= 0 || rateN <= 0) return 0;
  return Math.round((usdN / rateN) * 100) / 100;
}

export function applyDocumentLineFx<
  T extends {
    debitLocal: string;
    creditLocal: string;
    rate: string;
    debitUSD: string;
    creditUSD: string;
  },
>(line: T): T {
  return {
    ...line,
    debitUSD: formatLocalToUsd(line.debitLocal, line.rate),
    creditUSD: formatLocalToUsd(line.creditLocal, line.rate),
  };
}

export function applyDocumentLinePatch<
  T extends {
    debitLocal: string;
    creditLocal: string;
    rate: string;
    debitUSD: string;
    creditUSD: string;
  },
>(line: T, patch: Partial<T>): T {
  let next: T = { ...line, ...patch };
  const hasRate = toNumber(next.rate) > 0;

  if (patch.debitLocal != null && String(patch.debitLocal) !== '') {
    next = { ...next, creditLocal: '', creditUSD: '' };
  }
  if (patch.creditLocal != null && String(patch.creditLocal) !== '') {
    next = { ...next, debitLocal: '', debitUSD: '' };
  }
  if (patch.debitUSD != null && String(patch.debitUSD) !== '') {
    next = { ...next, creditLocal: '', creditUSD: '' };
  }
  if (patch.creditUSD != null && String(patch.creditUSD) !== '') {
    next = { ...next, debitLocal: '', debitUSD: '' };
  }

  const localTouched = 'debitLocal' in patch || 'creditLocal' in patch;
  const usdTouched = 'debitUSD' in patch || 'creditUSD' in patch;
  const rateTouched = 'rate' in patch;

  if (rateTouched) {
    next.debitUSD = formatLocalToUsd(next.debitLocal, next.rate);
    next.creditUSD = formatLocalToUsd(next.creditLocal, next.rate);
  } else if (usdTouched) {
    if ('debitUSD' in patch) {
      next.debitLocal =
        String(patch.debitUSD) === '' ? '' : hasRate ? formatUsdToLocal(patch.debitUSD, next.rate) : next.debitLocal;
    }
    if ('creditUSD' in patch) {
      next.creditLocal =
        String(patch.creditUSD) === '' ? '' : hasRate ? formatUsdToLocal(patch.creditUSD, next.rate) : next.creditLocal;
    }
  } else if (localTouched) {
    if ('debitLocal' in patch) {
      next.debitUSD =
        String(patch.debitLocal) === '' ? '' : formatLocalToUsd(next.debitLocal, next.rate);
    }
    if ('creditLocal' in patch) {
      next.creditUSD =
        String(patch.creditLocal) === '' ? '' : formatLocalToUsd(next.creditLocal, next.rate);
    }
  }

  return next;
}
